import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import OpenInNewTabButton from './OpenInNewTabButton'; // Keep this import
import { Button, useNotification } from '../common';
import { truncateUrl } from '../../utils/truncateUrl';

// Helper to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper to get current user ID from localStorage
const getCurrentUserId = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    const user = JSON.parse(userStr);
    return user.id;
  }
  return null;
};

const LeadDetailsModal = ({ 
  isOpen, 
  onClose, 
  lead, 
  users, 
  onLeadUpdate, 
  onOpenEditModal, 
  onOpenEmailModal
}) => {
  const { showNotification } = useNotification();
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const textareaRef = React.useRef(null);
  const [notesHistory, setNotesHistory] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Recent activity state
  const [emailHistory, setEmailHistory] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [taskHistory, setTaskHistory] = useState([]);
  const [dealHistory, setDealHistory] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activitiesPerPage] = useState(6);
  const [showEmailViewModal, setShowEmailViewModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedTaskToEdit, setSelectedTaskToEdit] = useState(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [dealCount, setDealCount] = useState(0);
  
  // Task creation state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: getTodayDate(),
    due_time: '23:59',
    priority: 'medium',
    assigned_to: '',
    lead_id: lead?.id,
    status: 'pending',
    type: 'task'
  });

  // Load current user from localStorage
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
      
      // Set the default assigned_to for new tasks to current user
      setNewTask(prevTask => ({
        ...prevTask,
        assigned_to: user.id
      }));
    }
  }, []);

  // Fetch notes and activity data when modal is opened
  useEffect(() => {
    if (isOpen && lead) {
      fetchNotesHistory();
      fetchActivityData();
      fetchDealCount();
      
      // Update lead_id in the task form when lead changes
      setNewTask(prevTask => ({
        ...prevTask,
        lead_id: lead.id
      }));
      
      // Add ESC key handler
      const handleEscKey = (e) => {
        if (e.key === 'Escape') {
          // If task modal is open, close only that modal
          if (isTaskModalOpen) {
            setIsTaskModalOpen(false);
          } else if (showEmailViewModal) {
            setShowEmailViewModal(false);
          } else if (showEditTaskModal) {
            setShowEditTaskModal(false);
          } else {
            // Otherwise close the lead details modal
            onClose();
          }
        }
      };
      
      // Add global event listener
      document.addEventListener('keydown', handleEscKey);
      
      // Initialize textarea height for auto-expanding
      if (textareaRef.current) {
        setTimeout(() => {
          textareaRef.current.style.height = '100px';
        }, 0);
      }
      
      // Cleanup on unmount or when modal closes
      return () => {
        document.removeEventListener('keydown', handleEscKey);
      };
    } else {
      // Clear data when modal closes
      setNotesHistory([]);
      setEmailHistory([]);
      setCallHistory([]);
      setTaskHistory([]);
      setDealHistory([]);
      setStatusHistory([]);
      setNewNote('');
      setIsTaskModalOpen(false);
      setActivityPage(1);
      setDealCount(0);
    }
  }, [isOpen, lead, onClose, isTaskModalOpen, showEmailViewModal, showEditTaskModal]);
  
  // Fetch all activity data for the lead
  const fetchActivityData = async () => {
    if (!lead) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      // Fetch all activity data in parallel
      const [emailRes, callRes, taskRes, dealRes, statusRes] = await Promise.all([
        axios.get(`/api/email/history/${lead.id}`, config),
        axios.get(`/api/leads/${lead.id}/call-history`, config),
        axios.get(`/api/tasks/lead/${lead.id}/history`, config),
        axios.get(`/api/deals/lead/${lead.id}/history`, config),
        axios.get(`/api/leads/${lead.id}/status-history`, config)
      ]);
      
      // Update state with fetched data
      setEmailHistory(emailRes.data || []);
      setCallHistory(callRes.data || []);
      setTaskHistory(taskRes.data || []);
      setDealHistory(dealRes.data || []);
      setStatusHistory(statusRes.data || []);
    } catch (err) {
      console.error('Error fetching activity data:', err);
    }
  };
  
  // Fetch deal count for this lead
  const fetchDealCount = async () => {
    if (!lead) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get('/api/leads/counts/deals', config);
      if (res.data && res.data[lead.id]) {
        setDealCount(res.data[lead.id]);
      } else {
        setDealCount(0);
      }
    } catch (error) {
      console.error('Error fetching deal count:', error);
      setDealCount(0);
    }
  };

  // Fetch notes history for selected lead
  const fetchNotesHistory = async () => {
    if (!lead) return;
    
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get(`/api/leads/${lead.id}/notes-history`, config);
      if (res.data && Array.isArray(res.data)) {
        setNotesHistory(res.data);
      }
    } catch (err) {
      console.log('Notes history not available', err);
      setNotesHistory([]);
    }
  };

  // Add new note
  const addNewNote = async () => {
    if (!newNote.trim() || !lead) return;
    
    try {
      setIsSavingNote(true);
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Add the new note
      await axios.post(
        `/api/leads/${lead.id}/notes`,
        { content: newNote },
        config
      );
      
      // Reset form and refresh notes
      setNewNote('');
      
      // Reset textarea height to default
      if (textareaRef.current) {
        textareaRef.current.style.height = '100px';
      }
      
      fetchNotesHistory();
      
      setIsSavingNote(false);
    } catch (err) {
      console.error('Error adding note:', err);
      setIsSavingNote(false);
      alert('Failed to add note. Please try again.');
    }
  };

  // Delete a note
  const deleteNote = async (noteId) => {
    // Use window.confirm instead of global confirm to avoid ESLint warning
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      await axios.delete(`/api/leads/${lead.id}/notes/${noteId}`, config);
      
      // Refresh notes list
      fetchNotesHistory();
    } catch (err) {
      console.error('Error deleting note:', err);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Format date for timestamps
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };
  
  // Handle email click from the activity list
  const handleEmailActivityClick = (email) => {
    setSelectedEmail(email);
    setShowEmailViewModal(true);
  };
  
  // Handle task click from the activity list
  const handleTaskActivityClick = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.get(`/api/tasks/${taskId}`, config);
      setSelectedTaskToEdit(res.data);
      setShowEditTaskModal(true);
    } catch (err) {
      console.error('Error fetching task details:', err);
      showNotification('Error loading task details', 'error');
    }
  };

  // Handle call attempts increment/decrement
  const updateCallAttempts = async (increment) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `/api/leads/${lead.id}/call-attempts`,
        { increment },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      // Get updated lead data
      let updatedLead = res.data;
      
      // Check if we need to update status to "unreachable" (if call attempts reaches 7)
      if (increment && updatedLead.call_attempts >= 7 && updatedLead.status !== 'unreachable') {
        // Update the lead status to unreachable
        const statusRes = await axios.put(
          `/api/leads/${lead.id}`,
          { ...updatedLead, status: 'unreachable' },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          }
        );
        updatedLead = statusRes.data;
      }
      
      // Notify parent component of the update
      onLeadUpdate(updatedLead);
    } catch (err) {
      console.error('Error updating call attempts:', err);
    }
  };

  // Update subscription status
  const updateSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(
        `/api/leads/${lead.id}`,
        { subscribed: !lead.subscribed },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      // Notify parent component of the update
      onLeadUpdate(res.data);
    } catch (err) {
      console.error('Error updating subscription status:', err);
    }
  };
  
  // Update lead status
  const updateLeadStatus = async (newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Check if this is a reconversion
      const isReconversion = newStatus === 'converted' && 
        lead.status !== 'converted' && 
        lead.status !== 'new' &&
        lead.reconversion_date === null;
      
      // Prepare update data
      const updateData = { 
        ...lead, 
        status: newStatus 
      };
      
      // If it's a reconversion, set the reconversion_date
      if (isReconversion) {
        updateData.reconversion_date = new Date().toISOString();
      }
      
      // Update the lead
      const response = await axios.put(
        `/api/leads/${lead.id}`,
        updateData,
        config
      );
      
      // Create local updated lead object that will be shown immediately in the UI
      const updatedLead = {
        ...lead,
        status: newStatus,
        reconversion_date: isReconversion ? new Date().toISOString() : lead.reconversion_date
      };
      
      // Notify parent component of the update with the local updated lead
      // This ensures the UI updates immediately with the new status
      onLeadUpdate(updatedLead);
      
    } catch (err) {
      console.error('Error updating lead status:', err);
    }
  };

  // Convert legacy note to new format
  const convertLegacyNote = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Add legacy note as a new note
      await axios.post(
        `/api/leads/${lead.id}/notes`,
        { content: lead.notes },
        config
      );
      
      // Clear the legacy notes field
      await axios.patch(
        `/api/leads/${lead.id}/notes`,
        { notes: '' },
        config
      );
      
      // Create updated lead object
      const updatedLead = {
        ...lead,
        notes: ''
      };
      
      // Notify parent component of the update
      onLeadUpdate(updatedLead);
      
      // Refresh notes history
      fetchNotesHistory();
    } catch (err) {
      console.error('Error converting legacy note:', err);
      alert('Failed to convert note. Please try again.');
    }
  };
  
  // Handle task form input changes
  const handleTaskInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask({
      ...newTask,
      [name]: value
    });
  };
  
  // Handle task form submission
  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSavingTask(true);
      const token = localStorage.getItem('token');
      
      // Properly format the task data
      const taskData = {
        ...newTask,
        // Don't include due_date in the form we're currently using it
        // since it's causing "Invalid Date" errors
        due_at: `${newTask.due_date}T${newTask.due_time}:00`
      };
      
      // Remove the original due_date field to avoid confusion
      delete taskData.due_date;
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };
      
      // Create the task
      await axios.post('/api/tasks', taskData, config);
      
      // Reset form
      setNewTask({
        title: '',
        description: '',
        due_date: getTodayDate(),
        due_time: '23:59',
        priority: 'medium',
        assigned_to: currentUser?.id || '',
        lead_id: lead.id,
        status: 'pending',
        type: 'task'
      });
      
      // Close modal and reset state
      setIsTaskModalOpen(false);
      setIsSavingTask(false);
      
      // Show success message using the shared notification system
      showNotification('Task created successfully', 'success');
      
    } catch (err) {
      console.error('Error creating task:', err);
      setIsSavingTask(false);
      showNotification('Failed to create task. Please try again.', 'error');
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center"
      style={{zIndex: 50}}
      tabIndex="-1"
      onClick={(e) => {
        // Only close if clicking directly on the background overlay
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 md:mx-0 p-6 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <h3 className="text-lg font-medium text-gray-900 mr-3">Lead Details</h3>
            {/* Use the dedicated OpenInNewTabButton component */}
            <OpenInNewTabButton leadId={lead.id} onClose={onClose} />
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 overflow-hidden flex-grow min-h-0">
          <div className="w-full md:w-3/5 overflow-y-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Name</h4>
                <p className="text-base text-gray-900">{lead.name}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Company</h4>
                <p className="text-base text-gray-900">{lead.company || '—'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Role</h4>
                <p className="text-base text-gray-900">{lead.role || '—'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Email</h4>
                <div className="flex items-center">
                  {lead.email && (
                    <button
                      onClick={onOpenEmailModal}
                      className="text-blue-500 hover:text-blue-700 mr-2"
                      title="Send email to this lead"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </button>
                  )}
                  <span className="text-base text-gray-900">{lead.email || '—'}</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Associated Deals</h4>
                <div className="text-base text-gray-900">
                  {dealCount > 0 ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {dealCount}
                    </span>
                  ) : '—'}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Secondary Email</h4>
                <div className="flex items-center">
                  {lead.secondary_email && (
                    <button
                      onClick={onOpenEmailModal}
                      className="text-blue-500 hover:text-blue-700 mr-2"
                      title="Send email to secondary address"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    </button>
                  )}
                  <span className="text-base text-gray-900">{lead.secondary_email || '—'}</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Phone</h4>
                <div className="flex items-center">
                  {lead.phone && (
                    <a 
                      href={`tel:${lead.phone}`} 
                      className="text-blue-500 hover:text-blue-700 mr-2" 
                      title="Call this number (will increment call attempts)"
                      onClick={() => updateCallAttempts(true)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </a>
                  )}
                  <p className="text-base text-gray-900">{lead.phone || '—'}</p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Country</h4>
                <p className="text-base text-gray-900">{lead.country || '—'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Original Source</h4>
                <p className="text-base text-gray-900 truncate" title={lead.original_source}>
                  {truncateUrl(lead.original_source).text}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Latest Source</h4>
                <p className="text-base text-gray-900 truncate" title={lead.latest_source}>
                  {truncateUrl(lead.latest_source).text}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Status</h4>
                <div className="relative inline-block">
                  <select
                    value={lead.status}
                    onChange={(e) => updateLeadStatus(e.target.value)}
                    className={`pl-2 pr-8 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer ${
                      {
                        'new': 'bg-blue-100 text-blue-800',
                        'connected': 'bg-yellow-100 text-yellow-800',
                        'HOT': 'bg-red-100 text-red-800',
                        'disqualified': 'bg-gray-100 text-gray-800',
                        'converted': 'bg-green-100 text-green-800',
                        'unreachable': 'bg-gray-100 text-gray-800'
                      }[lead.status] || 'bg-gray-100 text-gray-800'
                    } border-none focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none`}
                    title="Click to change status"
                  >
                    <option value="new">New</option>
                    <option value="connected">Connected</option>
                    <option value="HOT">HOT 🔥</option>
                    <option value="unreachable">Unreachable</option>
                    <option value="disqualified">Disqualified</option>
                    <option value="converted">Converted</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-1 text-gray-700">
                    <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Assigned To</h4>
                <p className="text-base text-gray-900">
                  {users
                    .filter(user => user.role === 'sales' || user.role === 'superuser' || user.role === 'admin')
                    .find(user => user.id === lead.assigned_to)?.name || '—'}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Created</h4>
                <p className="text-base text-gray-900">{new Date(lead.created_at).toLocaleString()}</p>
              </div>
              {lead.reconversion_date && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Reconversion Date</h4>
                  <p className="text-base text-orange-600 font-medium">{new Date(lead.reconversion_date).toLocaleString()}</p>
                </div>
              )}
              <div>
                <h4 className="text-sm font-medium text-gray-500">Subscription Status</h4>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                  lead.subscribed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {lead.subscribed ? 'Subscribed' : 'Not Subscribed'}
                </span>
                <Button
                  onClick={updateSubscriptionStatus}
                  variant="link"
                  className="ml-2 text-xs"
                >
                  {lead.subscribed ? 'Unsubscribe' : 'Subscribe'}
                </Button>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Call Attempts</h4>
                <div className="flex items-center mt-1 space-x-3">
                  <div className="flex items-center space-x-1">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center font-medium text-blue-700">
                      {lead.call_attempts || 0}
                    </div>
                    <span className="text-xs text-gray-500">of 10</span>
                  </div>
                  
                  <div className="flex space-x-1">
                    <Button
                      variant="outline"
                      onClick={() => updateCallAttempts(false)}
                      disabled={(lead.call_attempts || 0) <= 0}
                      className={`w-8 h-8 flex items-center justify-center rounded-md ${ 
                        (lead.call_attempts || 0) <= 0 ? 'cursor-not-allowed' : ''
                      }`}
                      title="Decrement call attempt"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                      </svg>
                    </Button>
                    
                    <Button
                      variant="outline"
                      onClick={() => updateCallAttempts(true)}
                      disabled={(lead.call_attempts || 0) >= 10}
                      className={`w-8 h-8 flex items-center justify-center rounded-md ${ 
                        (lead.call_attempts || 0) >= 10 ? 'cursor-not-allowed' : ''
                      }`}
                      title="Increment call attempt"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </Button>
                  </div>
                </div>
              </div>
        </div>
        
        {/* Notes Section with New Notes System */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Notes</h4>
          
          {/* Legacy Note Conversion - show only if there's a legacy note */}
          {lead.notes && (
            <div className="border-b border-gray-200 pb-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <h5 className="text-xs font-medium text-gray-500">Legacy Note</h5>
                <Button
                  type="button"
                  onClick={convertLegacyNote}
                  variant="link"
                  className="text-xs flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                  </svg>
                  Convert to New Format
                </Button>
              </div>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded whitespace-pre-wrap">
                {lead.notes}
              </div>
              <p className="mt-2 text-xs text-gray-500 italic">
                This note is in the legacy format. Click "Convert to New Format" to move it to the new notes system.
              </p>
            </div>
          )}
          
          {/* Notes Section - Side by Side Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* New Note Form */}
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-2">Add New Note</h5>
              <textarea
                ref={textareaRef}
                value={newNote}
                onChange={(e) => {
                  setNewNote(e.target.value);
                  
                  // Auto-adjust textarea height based on content
                  e.target.style.height = 'auto';
                  e.target.style.height = (e.target.scrollHeight) + 'px';
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 overflow-hidden"
                rows="4"
                style={{ minHeight: '100px', resize: 'none' }}
                placeholder="Write a new note..."
              ></textarea>
              <div className="mt-2 flex justify-end">
                <Button
                  onClick={addNewNote}
                  disabled={!newNote.trim() || isSavingNote}
                  variant="primary"
                  size="sm"
                  isLoading={isSavingNote}
                >
                  {isSavingNote ? 'Saving...' : 'Add Note'}
                </Button>
              </div>
            </div>

            {/* Notes History */}
            <div>
              <h5 className="text-xs font-medium text-gray-500 mb-2">Notes History</h5>
              <div className="space-y-3 max-h-72 overflow-y-auto">
                {notesHistory.length > 0 ? (
                  notesHistory.map((note) => (
                    <div key={note.id} className="bg-gray-50 p-3 rounded relative group">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-medium text-gray-900">
                          {note.user_name || 'Unknown User'}
                        </span>
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">
                            {formatDate(note.timestamp)}
                          </span>
                          {/* Delete button - only visible on hover for creators, admins, superusers */}
                          {currentUser && 
                            (currentUser.id === note.user_id || 
                             ['admin', 'superuser'].includes(currentUser.role)) && (
                            <button
                              type="button"
                              onClick={() => deleteNote(note.id)}
                              className="invisible group-hover:visible text-gray-400 hover:text-red-500 ml-2"
                              title="Delete note"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap">
                        {note.content}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 italic">No notes yet</div>
                )}
              </div>
            </div>
          </div>
        </div>
          </div>
          
          {/* Recent Activity Sidebar */}
          <div className="w-full md:w-2/5 flex flex-col">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
              </div>
              <div className="flex flex-col h-full">
                <div className="p-4 overflow-y-auto flex-1 min-h-0">
                {(() => {
                  // Combine all activities and sort by timestamp (newest first)
                  const allActivities = [
                    ...notesHistory.map(entry => ({ 
                      type: 'note', 
                      timestamp: entry.timestamp, 
                      content: entry.content,
                      user_name: entry.user_name,
                      id: entry.id
                    })),
                    ...emailHistory.map(email => ({ 
                      type: 'email', 
                      timestamp: email.created_at, 
                      content: email.subject || 'Email', 
                      email,
                      id: email.id
                    })),
                    ...callHistory.map(call => ({ 
                      type: 'call', 
                      timestamp: call.call_time, 
                      content: 'Call attempt',
                      user_name: call.user_name,
                      id: call.id
                    })),
                    ...taskHistory.map(task => ({ 
                      type: 'task', 
                      timestamp: task.timestamp, 
                      content: task.action,
                      user_name: task.user_name,
                      task_id: task.task_id,
                      id: task.id,
                      isTaskCreation: task.action && (
                        task.action.toLowerCase().includes('created') || 
                        task.action.toLowerCase().includes('added')
                      )
                    })),
                    ...dealHistory.map(deal => ({ 
                      type: 'deal', 
                      timestamp: deal.timestamp, 
                      content: deal.action || 'Deal created',
                      user_name: deal.user_name,
                      id: deal.id,
                      isDealCreation: !deal.action || deal.action.includes('created')
                    })),
                    ...statusHistory.map(status => ({ 
                      type: 'status', 
                      timestamp: status.created_at, 
                      content: `Status changed to ${status.new_status}`,
                      user_name: status.user_name,
                      id: status.id
                    }))
                  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                
                  // Paginate
                  const totalPages = Math.ceil(allActivities.length / activitiesPerPage);
                  const indexOfLastActivity = activityPage * activitiesPerPage;
                  const indexOfFirstActivity = indexOfLastActivity - activitiesPerPage;
                  const currentActivities = allActivities.slice(indexOfFirstActivity, indexOfLastActivity);
                  
                  if (currentActivities.length === 0) {
                    return (
                      <div className="py-4 text-center text-gray-500">
                        No activity recorded for this lead
                      </div>
                    );
                  }
                  
                  return (
                    <>
                      <ul className="space-y-3">
                        {currentActivities.map((activity, index) => (
                          <li key={`${activity.type}_${activity.id || index}`} className="border-b border-gray-100 pb-3 last:border-b-0">
                            <div className="flex items-start">
                              {/* Activity Icon */}
                              <div className="mr-2 mt-0.5">
                                {activity.type === 'note' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                                {activity.type === 'email' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700 cursor-pointer" onClick={() => handleEmailActivityClick(activity.email)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                  </span>
                                )}
                                {activity.type === 'call' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 text-yellow-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                    </svg>
                                  </span>
                                )}
                                {activity.type === 'task' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-purple-700 cursor-pointer" onClick={() => activity.task_id && handleTaskActivityClick(activity.task_id)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                                {activity.type === 'deal' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                                {activity.type === 'status' && (
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                              </div>
                              
                              {/* Activity Content */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {activity.type === 'note' ? (
                                    <>{activity.user_name || 'System'} added a note</>
                                  ) : activity.type === 'deal' && activity.isDealCreation ? (
                                    <>Deal created by {activity.user_name || 'System'}</>
                                  ) : activity.type === 'call' ? (
                                    <>Call attempt by {activity.user_name || 'System'}</>
                                  ) : activity.type === 'task' ? (
                                    <>Task created by {activity.user_name || 'System'}</>
                                  ) : (
                                    <>{activity.user_name || 'System'} {activity.content}</>
                                  )}
                                </p>
                                {activity.type === 'note' && (
                                  <p className="mt-1 text-sm text-gray-600 break-words">
                                    {activity.content.length > 100 
                                      ? `${activity.content.substring(0, 100)}...` 
                                      : activity.content}
                                  </p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                  {formatDate(activity.timestamp)}
                                </p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                      
                    </>
                  );
                })()}
                </div>
                
                {/* Pagination Controls - Moved outside the scrollable area */}
                <div className="px-4 pb-4 pt-2 border-t border-gray-200 mt-auto">
                  <div className="flex items-center justify-between">
                    <button 
                      onClick={() => setActivityPage(prev => Math.max(prev - 1, 1))}
                      disabled={activityPage === 1}
                      className={`px-3 py-1 text-sm rounded ${
                        activityPage === 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Page {activityPage} of {Math.max(1, Math.ceil(
                        (notesHistory.length + emailHistory.length + callHistory.length + 
                         taskHistory.length + dealHistory.length + statusHistory.length) / activitiesPerPage
                      ))}
                    </span>
                    <button
                      onClick={() => setActivityPage(prev => Math.min(prev + 1, Math.ceil(
                        (notesHistory.length + emailHistory.length + callHistory.length + 
                         taskHistory.length + dealHistory.length + statusHistory.length) / activitiesPerPage
                      )))}
                      disabled={activityPage >= Math.ceil(
                        (notesHistory.length + emailHistory.length + callHistory.length + 
                         taskHistory.length + dealHistory.length + statusHistory.length) / activitiesPerPage
                      )}
                      className={`px-3 py-1 text-sm rounded ${
                        activityPage >= Math.ceil(
                          (notesHistory.length + emailHistory.length + callHistory.length + 
                           taskHistory.length + dealHistory.length + statusHistory.length) / activitiesPerPage
                        )
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between space-x-3 mt-4 flex-shrink-0">
          <button
            onClick={() => setIsTaskModalOpen(true)}
            className="bg-amber-50 hover:bg-amber-100 text-amber-700 py-2 px-4 rounded flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
            New Task
          </button>
          
          <div className="flex space-x-3">
            <Button
              onClick={onOpenEditModal}
              variant="outline"
              className="text-blue-600 border-blue-600 hover:bg-blue-50 px-4 py-2"
            >
              Edit Lead
            </Button>
            <Button
              onClick={onClose}
              variant="secondary"
              className="!bg-gray-500 hover:!bg-gray-600 px-4 py-2"
            >
              Close
            </Button>
          </div>
        </div>
        
        {/* New Task Modal */}
        {isTaskModalOpen && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center" 
            style={{zIndex: 60}}
            onClick={(e) => {
              // Close modal when clicking the overlay (outside the modal)
              if (e.target === e.currentTarget) {
                setIsTaskModalOpen(false);
              }
            }}
          >
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Task for {lead.name}</h3>
                <button 
                  onClick={() => setIsTaskModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleTaskSubmit}>
                <div className="space-y-4">
                  {/* Task Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      id="title"
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={newTask.title}
                      onChange={handleTaskInputChange}
                    />
                  </div>
                  
                  {/* Task Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows="3"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={newTask.description}
                      onChange={handleTaskInputChange}
                    ></textarea>
                  </div>
                  
                  {/* Due Date and Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="due_date" className="block text-sm font-medium text-gray-700">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        name="due_date"
                        id="due_date"
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={newTask.due_date}
                        onChange={handleTaskInputChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="due_time" className="block text-sm font-medium text-gray-700">
                        Due Time *
                      </label>
                      <input
                        type="time"
                        name="due_time"
                        id="due_time"
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={newTask.due_time}
                        onChange={handleTaskInputChange}
                      />
                    </div>
                  </div>
                  
                  {/* Priority and Assigned To */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                        Priority
                      </label>
                      <select
                        name="priority"
                        id="priority"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={newTask.priority}
                        onChange={handleTaskInputChange}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700">
                        Assign To
                      </label>
                      <select
                        name="assigned_to"
                        id="assigned_to"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={newTask.assigned_to}
                        onChange={handleTaskInputChange}
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Task Type */}
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <select
                      name="type"
                      id="type"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      value={newTask.type}
                      onChange={handleTaskInputChange}
                    >
                      <option value="task">Task</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                </div>
                
                <div className="mt-5 flex justify-end space-x-3">
                  <Button
                    type="button"
                    onClick={() => setIsTaskModalOpen(false)}
                    variant="outline"
                    className="px-4 py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSavingTask}
                    disabled={isSavingTask || !newTask.title || !newTask.due_date || !newTask.due_time}
                    className="px-4 py-2"
                  >
                    {isSavingTask ? 'Creating...' : 'Create Task'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Edit Task Modal */}
        {showEditTaskModal && selectedTaskToEdit && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-60"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEditTaskModal(false);
              }
            }}
          >
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Task Details</h3>
                <button 
                  onClick={() => setShowEditTaskModal(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Title</h4>
                  <p className="text-base text-gray-900">{selectedTaskToEdit.title}</p>
                </div>
                {selectedTaskToEdit.description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Description</h4>
                    <p className="text-base text-gray-900 whitespace-pre-wrap">{selectedTaskToEdit.description}</p>
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Due Date</h4>
                  <p className="text-base text-gray-900">
                    {selectedTaskToEdit.due_at && new Date(selectedTaskToEdit.due_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Priority</h4>
                  <p className="text-base text-gray-900 capitalize">{selectedTaskToEdit.priority}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Status</h4>
                  <p className="text-base text-gray-900 capitalize">{selectedTaskToEdit.status?.replace('_', ' ')}</p>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowEditTaskModal(false)}
                    variant="primary"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Email View Modal */}
        {showEmailViewModal && selectedEmail && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-60"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowEmailViewModal(false);
              }
            }}
          >
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Email Details</h3>
                <button 
                  onClick={() => setShowEmailViewModal(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Subject</h4>
                  <p className="text-base text-gray-900">{selectedEmail.subject || 'No subject'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">From</h4>
                  <p className="text-base text-gray-900">{selectedEmail.from_name || selectedEmail.from}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">To</h4>
                  <p className="text-base text-gray-900">{selectedEmail.to}</p>
                </div>
                {selectedEmail.cc && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">CC</h4>
                    <p className="text-base text-gray-900">{selectedEmail.cc}</p>
                  </div>
                )}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Date</h4>
                  <p className="text-base text-gray-900">{formatDate(selectedEmail.created_at)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Message</h4>
                  <div 
                    className="mt-2 text-sm text-gray-800 p-4 border border-gray-200 rounded bg-gray-50 max-h-96 overflow-y-auto" 
                    dangerouslySetInnerHTML={{ __html: selectedEmail.message || selectedEmail.text || 'No content' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

LeadDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  lead: PropTypes.object,
  users: PropTypes.array.isRequired,
  onLeadUpdate: PropTypes.func.isRequired,
  onOpenEditModal: PropTypes.func.isRequired,
  onOpenEmailModal: PropTypes.func.isRequired
};

export default LeadDetailsModal;
